/**
 * WlPartnerLogosCard — Gerenciamento de logomarcas de parceiros (painel WL).
 *
 * Permite upload, edição, reordenação (via setas) e ativação/inativação de
 * logos exibidas na seção de social proof da página de planos do tenant.
 */
import { useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { useWlPartnerLogos } from "@/hooks/wl/useWlPartnerLogos";
import { toast } from "@/hooks/use-toast";

const MAX_BYTES = 1 * 1024 * 1024; // 1 MB
const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";

export default function WlPartnerLogosCard() {
  const { logos, isLoading, uploadLogo, upsert, remove, reorder } = useWlPartnerLogos();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setName("");
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePickFile = (f: File | null) => {
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      toast({
        title: "Arquivo muito grande",
        description: "Tamanho máximo: 1 MB",
        variant: "destructive",
      });
      return;
    }
    setFile(f);
    if (!name) {
      // Sugere nome a partir do arquivo
      const base = f.name.replace(/\.[^.]+$/, "");
      setName(base.slice(0, 80));
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast({ title: "Selecione uma imagem", variant: "destructive" });
      return;
    }
    if (!name.trim()) {
      toast({ title: "Informe o nome do parceiro", variant: "destructive" });
      return;
    }
    try {
      setUploading(true);
      const { url } = await uploadLogo(file);
      await upsert.mutateAsync({
        name: name.trim(),
        logo_url: url,
        display_order: logos.length, // Adiciona no fim
        is_active: true,
      });
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      // Toasts já são exibidos pelos hooks
    } finally {
      setUploading(false);
    }
  };

  const handleToggle = (id: string, name: string, logo_url: string, next: boolean) => {
    upsert.mutate({ id, name, logo_url, is_active: next });
  };

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= logos.length) return;
    const next = [...logos];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    reorder.mutate(next.map((l) => l.id));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Logomarcas de Parceiros</CardTitle>
          <CardDescription>
            Logos exibidas na seção de autoridade da página de planos. Recomendado: PNG/SVG com fundo transparente.
          </CardDescription>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="shrink-0">
          <Plus className="size-4 mr-2" />
          Adicionar parceiro
        </Button>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : logos.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-muted-foreground border-2 border-dashed border-border rounded-lg">
            <ImagePlus className="size-10" />
            <div>
              <p className="font-medium text-foreground">Nenhuma logomarca cadastrada</p>
              <p className="text-sm">
                Clique em "Adicionar parceiro" para enviar a primeira logo.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {logos.map((logo, idx) => (
              <div
                key={logo.id}
                className="group relative flex flex-col rounded-lg border border-border bg-card overflow-hidden"
              >
                <div className="flex items-center justify-center h-28 bg-white p-4">
                  <img
                    src={logo.logo_url}
                    alt={logo.name}
                    className="max-h-full max-w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="flex items-center justify-between gap-2 p-3 border-t border-border">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{logo.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Switch
                        checked={logo.is_active}
                        onCheckedChange={(v) =>
                          handleToggle(logo.id, logo.name, logo.logo_url, v)
                        }
                      />
                      <span className="text-xs text-muted-foreground">
                        {logo.is_active ? "Ativo" : "Inativo"}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      disabled={idx === 0}
                      onClick={() => move(idx, -1)}
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      disabled={idx === logos.length - 1}
                      onClick={() => move(idx, 1)}
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8 text-destructive hover:text-destructive"
                    onClick={() => setDeleteId(logo.id)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialog de upload */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) resetForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar parceiro</DialogTitle>
            <DialogDescription>
              Envie a logomarca (PNG, SVG, WEBP ou JPG, máx. 1 MB).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="partner-name">Nome do parceiro</Label>
              <Input
                id="partner-name"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 80))}
                placeholder="Ex.: DHL"
                maxLength={80}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Logomarca</Label>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full h-32 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-accent/30 transition-colors"
              >
                {file ? (
                  <>
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      className="max-h-16 max-w-[160px] object-contain"
                    />
                    <span className="text-xs text-muted-foreground">{file.name}</span>
                  </>
                ) : (
                  <>
                    <Upload className="size-6 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Clique para selecionar a imagem
                    </span>
                  </>
                )}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => handlePickFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={uploading}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={uploading || !file || !name.trim()}>
              {uploading ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
              Salvar parceiro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover parceiro?</AlertDialogTitle>
            <AlertDialogDescription>
              A logomarca deixará de aparecer na página de planos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) remove.mutate(deleteId);
                setDeleteId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
